import { useState } from "react";
import classNames from "classnames";
import { LeftChevronSVG, RightChevronSVG } from "@/components/svg";
import Image from "next/image";
import { urlForImage } from "@/lib/sanity";
import { StarRating } from "./svg";

export default function CarouselGoogleReview({ reviews, showButtons = true }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleNextButton = () => {
    setSelectedIndex((selectedIndex) => {
      if (selectedIndex === reviews.length - 1) return 0;
      else return selectedIndex + 1;
    });
  };
  const handleBackButton = () => {
    setSelectedIndex((selectedIndex) => {
      if (selectedIndex === 0) return reviews.length - 1;
      else return selectedIndex - 1;
    });
  };

  const handleCardClick = (index) => {
    if (index === selectedIndex) return;
    setIsAnimating(true);
    setSelectedIndex(index);
    setTimeout(() => {
      setIsAnimating(false);
    }, 700);
  };

  const isNextCard = (index, selectedIndex, array) => {
    if (selectedIndex === array.length - 1) return index === 0;
    else return index === selectedIndex + 1;
  };
  const isPrevCard = (index, selectedIndex, array) => {
    if (selectedIndex === 0) return index === array.length - 1;
    else return index === selectedIndex - 1;
  };

  const getRelativeCardPosition = (index, selectedIndex, array) => {
    if (index === selectedIndex) return 0;
    if (isPrevCard(index, selectedIndex, array)) return -1;
    if (isNextCard(index, selectedIndex, array)) return 1;
    return 3;
  };

  const handleCardPositions = (index, selectedIndex, array) => {
    //3 cards on screen
    //  -28 0 28
    // i-1 i i+1
    const relativePos = getRelativeCardPosition(index, selectedIndex, array);
    switch (relativePos) {
      case 0:
        return "md:scale-[1.30] z-[3] hover:bg-background-gray";
      case -1:
        return `-translate-x-96 lg:-translate-x-28 opacity-0 lg:opacity-100 z-[2] ${
          showButtons && "pointer-events-none"
        }`;
      case 1:
        return `translate-x-96 lg:translate-x-28 opacity-0 lg:opacity-100 z-[1] ${
          showButtons && "pointer-events-none"
        }`;
      default:
        return "opacity-0";
    }
  };

  return (
    <div className="w-full">
      <div className="w-[calc{100%)] mx-auto relative">
        {showButtons && (
          <button
            className={classNames(
              "absolute top-[50%] -translate-y-[50%] left-0 md:translate-x-16 lg:translate-x-10 rounded z-20",
              "hover:bg-white/40",
              "hover:text-dark-purple",
              "transition-all ease duration-300"
            )}
            onClick={() => handleBackButton()}
            aria-label="Previous review"
          >
            <LeftChevronSVG className="h-[42px] w-[24px] md:w-[30px] stroke-white" />
          </button>
        )}
        <div className="scene w-full h-[365px] lg:h-[360px]">
          <div className="carousel overflow-hidden h-full w-full">
            {reviews.map((ele, index) => {
              return (
                <div
                  key={ele._key}
                  className={classNames(
                    "carousel__cell w-[70%] max-w-[270px] md:max-w-full md:w-[198px]",
                    "h-[315px] sm:h-[320px] md:h-[261px] px-2.5 py-2.5 top-[50%]",
                    "-translate-y-[50%] left-0 right-0 mx-auto text-ellipsis overflow-hidden",
                    handleCardPositions(index, selectedIndex, reviews)
                  )}
                  onClick={!showButtons ? () => handleCardClick(index) : null}
                >
                  <a
                    target={isAnimating ? "" : "_blank"}
                    rel="noopener noreferrer"
                    href={
                      isAnimating
                        ? "javascript:void(0)"
                        : ele.callToActions && ele.callToActions[0].url
                    }
                  >
                    <div className="h-full">
                      <div className="flex items-center">
                        {ele.icon && (
                          <Image
                            src={urlForImage(ele.icon.asset._ref).url()}
                            width="25px"
                            height="25x"
                            alt="profile picture"
                          />
                        )}
                        <p
                          className={classNames(
                            "pl-2 text-sm md:text-xs font-['Arial'] text-left mt-0.5 md:mt-0",
                            index === selectedIndex
                              ? "text-[rgb(60,64,67)]"
                              : "text-subtext-gray"
                          )}
                        >
                          {ele.heading[0].children[0].text}
                        </p>
                      </div>
                      <div className="flex py-1.5">
                        {[...Array(5).keys()].map((i) => (
                          <StarRating
                            key={`star${i}`}
                            className="w-4 h-4 fill-yellow"
                          />
                        ))}
                      </div>
                      <div className="line-clamp-[12]">
                        <p
                          className={classNames(
                            "text-sm md:text-[10px] leading-normal font-['Arial'] text-left",
                            index === selectedIndex
                              ? "text-[rgb(60,64,67)]"
                              : "text-subtext-gray"
                          )}
                        >
                          {ele.content[0].children[0].text}
                        </p>
                      </div>
                    </div>
                  </a>
                </div>
              );
            })}
          </div>
        </div>
        {showButtons && (
          <button
            className={classNames(
              "absolute top-[50%] -translate-y-[50%] right-0 md:-translate-x-16 lg:-translate-x-10 rounded z-20",
              "hover:bg-white/40",
              "hover:text-dark-purple",
              "transition-all ease duration-300"
            )}
            onClick={() => handleNextButton()}
            aria-label="Next review"
          >
            <RightChevronSVG className="h-[42px] w-[24px] md:w-[30px] stroke-white" />
          </button>
        )}
      </div>
    </div>
  );
}
